/**
 * The next deduction, and how much of it to give away.
 *
 * The press promises a reader in trouble one step rather than the answer, in
 * stages: which clue to look at, then what it combines with, then the mark
 * itself. That promise is only keepable because the same solver that cleared
 * the puzzle for print can be re-run from wherever the reader has got to — so
 * this drives the real solver rather than reading the answer key and picking
 * something the reader has not written yet. The step it names is always a step
 * that is actually available from the marks on the page.
 *
 * The solver is imported through `@gen`, which reaches into the generator's
 * src/. Nothing in that chain may pull in pdf-lib — see site/vite.config.ts —
 * and solver.ts does not: it takes types, a grid, and a propagator.
 */
import type { Clue, Puzzle } from "@gen/logic-grid/types.ts";
import { drawnUpright } from "@gen/logic-grid/axes.ts";
import { Grid } from "@gen/logic-grid/solver.ts";
import {
  ALL_TECHNIQUES,
  NO_CONSTRAINTS,
  applyBetweens,
  applyConditionals,
  applyCounting,
  applyRelations,
  compile,
  propagate,
} from "@gen/logic-grid/solver-propagate.ts";
import {
  type Cell,
  cellKey,
  cellName,
  blockName,
  blocks,
  findMistakes,
  isSolved,
  type Marks,
  type Mistake,
  mistakeBlocks,
  resolve,
} from "./marks.ts";

export type Hint =
  /** Something on the page is wrong; nothing else is worth saying until it is not. */
  | { kind: "mistake"; mistakes: Mistake[]; blocks: string[] }
  /** Finished. */
  | { kind: "solved" }
  /** A clue the reader has not finished using. */
  | { kind: "clue"; clueIndex: number; clue: Clue; cell: Cell; mark: "tick" | "cross" }
  /** No clue needed — the marks in one block already force this. */
  | { kind: "grid"; cell: Cell; mark: "tick" | "cross"; forced: Forced }
  /**
   * Nothing found. Either the reader has marked their way into a position the
   * solver cannot advance with the techniques the book teaches, or the puzzle
   * needs a step this engine does not model. Says so rather than inventing one.
   */
  | { kind: "stuck" };

/**
 * Why a grid hint holds, which is the part a reader has to be able to check.
 *
 * "Only one possibility is open" is a claim about one line of one block, and
 * saying it without saying *which* line sent readers down the row when the
 * column was the forced one — the row still had two cells open, so the hint
 * read as simply wrong. `alongCat`/`alongVal` name the line; whether that is a
 * row or a column on the page depends on how the block is drawn.
 */
export type Forced =
  | { by: "line"; alongCat: number; alongVal: number }
  /**
   * Two ticks meeting on a shared value: the reader has tied 2nd to Greta and
   * 2nd to pink, so Greta and pink go together. This is the move solvers make
   * constantly and call "carrying it over", and it is the most immediate thing
   * a grid can offer after a line that has run out.
   */
  | { by: "carry"; bridgeCat: number; bridgeVal: number }
  /**
   * A tick and a cross meeting on a shared value: 2nd is Greta, and 2nd is not
   * gold, so Greta is not gold. The same single hop as a carry, giving a cross.
   */
  | { by: "rule-out"; bridgeCat: number; bridgeVal: number; tiedCat: number }
  /** Settled by marks across several blocks rather than by one line. */
  | { by: "combination" };

/**
 * How much of a hint has been shown. Each press of "another hint" adds one.
 *
 * Four, not three. The first three narrow down to the mark; the fourth is the
 * reasoning behind it, which is a different thing to want and worth having to
 * ask for. A reader who is merely stuck wants stage one. A reader who does not
 * believe the mark, or wants to be able to find the next one themselves, wants
 * stage four — and until it existed, all the page could do for them was repeat
 * the answer.
 */
export type Stage = 1 | 2 | 3 | 4;

/**
 * The reader's marks as a solver grid.
 *
 * Only ticks and crosses the reader has actually written — this is their
 * knowledge, not the answer. A tick eliminates the rest of its row and column
 * in that block, which is the same thing marks.ts does on the page.
 */
export function gridFromMarks(puzzle: Puzzle, marks: Marks): Grid {
  const grid = new Grid(puzzle.categories.length, puzzle.size);
  for (const [rowCat, colCat] of blocks(puzzle.categories.length)) {
    for (let rowVal = 0; rowVal < puzzle.size; rowVal++) {
      for (let colVal = 0; colVal < puzzle.size; colVal++) {
        const mark = marks[cellKey({ rowCat, rowVal, colCat, colVal })];
        if (mark === "cross") grid.eliminate(rowCat, colCat, rowVal, colVal);
        if (mark === "tick") {
          for (let v = 0; v < puzzle.size; v++) {
            if (v !== colVal) grid.eliminate(rowCat, colCat, rowVal, v);
            if (v !== rowVal) grid.eliminate(rowCat, colCat, v, colVal);
          }
        }
      }
    }
  }
  return grid;
}

/**
 * A tick the marks on the page already force, that the reader has not written.
 *
 * The last cross in a row leaves one cell standing, and that cell is a tick —
 * but only the crosses got written, so nothing downstream sees it as *new*
 * knowledge and every probe below reports no progress. Without this the engine
 * advises its way to about two thirds of a grid and then says it is stuck,
 * which is exactly where a reader needs it least.
 *
 * Both directions, because a block is square: three crosses down a column
 * force the fourth cell just as three across a row do. The grid stores each
 * elimination symmetrically, so the column is the row of the transpose.
 */
function firstForced(
  puzzle: Puzzle,
  marks: Marks,
  grid: Grid,
): { cell: Cell; forced: Forced } | null {
  for (const [rowCat, colCat] of blocks(puzzle.categories.length)) {
    for (let rowVal = 0; rowVal < puzzle.size; rowVal++) {
      for (let colVal = 0; colVal < puzzle.size; colVal++) {
        const cell = { rowCat, rowVal, colCat, colVal };
        if (marks[cellKey(cell)] !== undefined) continue;
        if (!grid.get(rowCat, colCat, rowVal, colVal)) continue;
        // Which line is exhausted decides what the reader is told to look at,
        // so it is carried out rather than recomputed from the cell.
        if (grid.candidates(rowCat, colCat, rowVal).length === 1) {
          return { cell, forced: { by: "line", alongCat: rowCat, alongVal: rowVal } };
        }
        if (grid.candidates(colCat, rowCat, colVal).length === 1) {
          return { cell, forced: { by: "line", alongCat: colCat, alongVal: colVal } };
        }
      }
    }
  }
  return null;
}

/** A cell `after` has settled that `before` had not, or null. */
function firstNewCell(puzzle: Puzzle, marks: Marks, before: Grid, after: Grid): Cell | null {
  let newCross: Cell | null = null;
  for (const [rowCat, colCat] of blocks(puzzle.categories.length)) {
    for (let rowVal = 0; rowVal < puzzle.size; rowVal++) {
      for (let colVal = 0; colVal < puzzle.size; colVal++) {
        const cell = { rowCat, rowVal, colCat, colVal };
        if (marks[cellKey(cell)] !== undefined) continue;
        const was = before.get(rowCat, colCat, rowVal, colVal);
        const now = after.get(rowCat, colCat, rowVal, colVal);
        // Settled to a tick: the only survivor in both its row and its column.
        if (
          now &&
          after.candidates(rowCat, colCat, rowVal).length === 1 &&
          before.candidates(rowCat, colCat, rowVal).length > 1
        ) {
          return cell;
        }
        // Ruled out. Held as a fallback — a tick is the better thing to be told.
        if (was && !now && !newCross) newCross = cell;
      }
    }
  }
  return newCross;
}

/** One square, named the way marks.ts names it: lower category first. */
function pairOf(c1: number, v1: number, c2: number, v2: number): Cell {
  return c1 < c2
    ? { rowCat: c1, rowVal: v1, colCat: c2, colVal: v2 }
    : { rowCat: c2, rowVal: v2, colCat: c1, colVal: v1 };
}

/**
 * A cell two of the reader's own ticks already settle between them.
 *
 * Propagation finds this too, but it finds everything else at the same time and
 * cannot say which was the short step. A reader who has just written 2nd = pink
 * wants to be shown that Greta = pink follows from it, not sent four links away
 * to a deduction that happens to sit in a lower-numbered block.
 */
function firstCarry(puzzle: Puzzle, marks: Marks): { cell: Cell; forced: Forced } | null {
  const K = puzzle.categories.length;
  const tie = pairOf;

  for (let bridgeCat = 0; bridgeCat < K; bridgeCat++) {
    for (let bridgeVal = 0; bridgeVal < puzzle.size; bridgeVal++) {
      // Everything the reader has already tied to this one value.
      const partners: Array<[number, number]> = [];
      for (let cat = 0; cat < K; cat++) {
        if (cat === bridgeCat) continue;
        for (let val = 0; val < puzzle.size; val++) {
          if (marks[cellKey(tie(bridgeCat, bridgeVal, cat, val))] === "tick") {
            partners.push([cat, val]);
          }
        }
      }
      for (let x = 0; x < partners.length; x++) {
        for (let y = x + 1; y < partners.length; y++) {
          const [c1, v1] = partners[x]!;
          const [c2, v2] = partners[y]!;
          if (c1 === c2) continue;
          const cell = tie(c1, v1, c2, v2);
          if (marks[cellKey(cell)] !== undefined) continue;
          return { cell, forced: { by: "carry", bridgeCat, bridgeVal } };
        }
      }
    }
  }
  return null;
}

/**
 * A cell ruled out by one tick and one cross meeting on a shared value.
 *
 * The mirror of `firstCarry`, and the same single hop, but it gives a cross
 * where a carry gives a tick — so a grid mid-solve usually offers dozens of
 * these, most of them true, checkable and completely useless. `opensALine`
 * separates the two: a rule-out worth interrupting a reader for is one that
 * takes a row or a column down to its last open cell, because the very next
 * hint is then the tick that line forces.
 *
 * Those go ahead of the clue list. The rest wait until the clues are spent,
 * where a cross that leads nowhere still beats saying nothing.
 */
function firstRuleOut(
  puzzle: Puzzle,
  marks: Marks,
  grid: Grid,
  opensALine: boolean,
): { cell: Cell; forced: Forced } | null {
  const K = puzzle.categories.length;

  /** Whether crossing this cell out would leave one cell open in some line. */
  const decisive = (cell: Cell): boolean => {
    const { rowCat, rowVal, colCat, colVal } = cell;
    if (!grid.get(rowCat, colCat, rowVal, colVal)) return false;
    const across = grid.candidates(rowCat, colCat, rowVal);
    const down = grid.candidates(colCat, rowCat, colVal);
    return (
      (across.length === 2 && across.includes(colVal)) ||
      (down.length === 2 && down.includes(rowVal))
    );
  };
  for (let bridgeCat = 0; bridgeCat < K; bridgeCat++) {
    for (let bridgeVal = 0; bridgeVal < puzzle.size; bridgeVal++) {
      for (let tiedCat = 0; tiedCat < K; tiedCat++) {
        if (tiedCat === bridgeCat) continue;
        for (let tiedVal = 0; tiedVal < puzzle.size; tiedVal++) {
          if (marks[cellKey(pairOf(bridgeCat, bridgeVal, tiedCat, tiedVal))] !== "tick") continue;
          for (let outCat = 0; outCat < K; outCat++) {
            if (outCat === bridgeCat || outCat === tiedCat) continue;
            for (let outVal = 0; outVal < puzzle.size; outVal++) {
              if (marks[cellKey(pairOf(bridgeCat, bridgeVal, outCat, outVal))] !== "cross")
                continue;
              const cell = pairOf(tiedCat, tiedVal, outCat, outVal);
              if (marks[cellKey(cell)] !== undefined) continue;
              if (opensALine && !decisive(cell)) continue;
              return { cell, forced: { by: "rule-out", bridgeCat, bridgeVal, tiedCat } };
            }
          }
        }
      }
    }
  }
  return null;
}

/**
 * The reader's grid with one clue read against it, and nothing more.
 *
 * The difference between this and a full `propagate` is the whole difference
 * between a hint that can be explained and one that cannot. Propagation applies
 * the clue and then runs every grid-wide rule until nothing moves, so a cell it
 * settles may be three or four deductions past the clue that unlocked it — true,
 * but not something a reader can see by looking at the clue and the block. What
 * comes out of here is only what the clue itself says, so there is always a
 * short answer to "why".
 *
 * Null when the clue contradicts the marks, which means the reader has a wrong
 * mark somewhere and `findMistakes` will have said so already.
 */
export function applyClueOnce(puzzle: Puzzle, marks: Marks, clue: Clue): Grid | null {
  const grid = gridFromMarks(puzzle, marks);
  const compiled = compile(grid, [clue]);
  if (compiled.contradiction) return null;
  if (applyRelations(grid, compiled.relations).contradiction) return null;
  if (applyBetweens(grid, compiled.betweens).contradiction) return null;
  if (applyConditionals(grid, compiled.conditionals).contradiction) return null;
  if (applyCounting(grid, compiled.countings).contradiction) return null;
  return grid;
}

/** What the reader should write in a cell the solver has settled. */
function markFor(after: Grid, cell: Cell): "tick" | "cross" {
  return after.get(cell.rowCat, cell.colCat, cell.rowVal, cell.colVal) ? "tick" : "cross";
}

/**
 * The next step available from these marks.
 *
 * Order matters. A mistake comes first, because everything after it would be
 * advice about a grid that cannot be finished — and a bad mark twenty minutes
 * ago is the usual reason a puzzle stops moving. Then clues, in the order they
 * are printed, so the reader is sent to the earliest one still carrying
 * something. Only if no clue can move does it fall back to a deduction the
 * marks alone force, which is a different kind of nudge: look at the block, not
 * the clue list.
 */
export function nextHint(puzzle: Puzzle, written: Marks): Hint {
  /*
   * Mistakes are looked for in what the reader actually wrote, not in the
   * resolved grid. A derived cross is only ever wrong because the tick that
   * implied it is wrong, so checking the written marks catches every mistake
   * there is — and blames the one click that caused it rather than reporting a
   * single bad tick as seven bad marks.
   */
  const mistakes = findMistakes(puzzle, written);
  if (mistakes.length > 0) {
    return { kind: "mistake", mistakes, blocks: mistakeBlocks(puzzle, mistakes) };
  }
  if (isSolved(puzzle, written)) return { kind: "solved" };

  /*
   * Everything below works from the resolved grid — what is on screen. Telling
   * a reader to cross out a cell that already shows a cross would be no help,
   * and most crosses on a well-marked grid are derived rather than written.
   */
  const marks = resolve(puzzle, written);
  const base = gridFromMarks(puzzle, marks);

  /*
   * Before reaching for a clue: is there a cell the marks already on the page
   * settle? Sending a reader to the clue list for something their own grid
   * answers would be bad advice, and it is the commonest step in the back half
   * of a puzzle.
   */
  const forced = firstForced(puzzle, marks, base);
  if (forced) return { kind: "grid", cell: forced.cell, mark: "tick", forced: forced.forced };

  /*
   * Then the short step: two ticks that already meet. Propagation below would
   * find it as well, but only alongside everything else the marks imply, and
   * with no way to tell that this one was a single hop.
   */
  const carried = firstCarry(puzzle, marks);
  if (carried) return { kind: "grid", cell: carried.cell, mark: "tick", forced: carried.forced };

  /*
   * And the same hop the other way round, when it settles a line. A clue hint
   * is only honest if the reader can get from the clue to the mark in one look,
   * and the clue probes below propagate the whole grid — so a clue can be
   * credited with a conclusion that is really three grid moves past it. Taking
   * those moves first is what keeps the clue hint checkable.
   */
  const opening = firstRuleOut(puzzle, marks, base, true);
  if (opening) return { kind: "grid", cell: opening.cell, mark: "cross", forced: opening.forced };

  /*
   * What the marks force on their own, with no clue read at all: pair
   * uniqueness and transitivity. This is both a candidate hint and the control
   * for the clue probes below — without it, every clue would appear to "work",
   * since propagating any one of them also runs these same grid-wide rules.
   */
  const gridOnly = base.clone();
  const gridOnlyOk = !propagate(gridOnly, NO_CONSTRAINTS).contradiction;

  const control = gridOnlyOk ? gridOnly : base;

  /*
   * First pass: a cell the clue settles by itself, in one reading, with no
   * propagation afterwards. This is the only kind of clue hint that can be
   * explained — see lib/explain.ts — because it is the only kind where the
   * reader gets from the printed line to the square in one move. The second
   * pass below still exists, and about a quarter of clue hints come from it,
   * but a hint that can be justified beats one that merely happens to be true.
   */
  for (const [index, clue] of puzzle.clues.entries()) {
    const once = applyClueOnce(puzzle, marks, clue);
    if (!once) continue;
    const cell = firstNewCell(puzzle, marks, control, once);
    if (cell) return { kind: "clue", clueIndex: index, clue, cell, mark: markFor(once, cell) };
  }

  for (const [index, clue] of puzzle.clues.entries()) {
    const probe = base.clone();
    const compiled = compile(probe, [clue]);
    if (compiled.contradiction) continue;
    const result = propagate(probe, compiled, ALL_TECHNIQUES);
    if (result.contradiction) continue;
    // Measured against gridOnly, not base, so a deduction the reader could have
    // made without reading anything is not blamed on this clue.
    const cell = firstNewCell(puzzle, marks, control, probe);
    if (cell) return { kind: "clue", clueIndex: index, clue, cell, mark: markFor(probe, cell) };
  }

  // Any remaining single hop, before anything needing marks combined.
  const ruled = firstRuleOut(puzzle, marks, base, false);
  if (ruled) return { kind: "grid", cell: ruled.cell, mark: "cross", forced: ruled.forced };

  if (gridOnlyOk) {
    // Nothing short is left: this is what pair uniqueness and transitivity
    // settle between them, several steps from any one mark.
    const cell = firstNewCell(puzzle, marks, base, gridOnly);
    if (cell)
      return { kind: "grid", cell, mark: markFor(gridOnly, cell), forced: { by: "combination" } };
  }

  return { kind: "stuck" };
}

/**
 * Which line of a block a reader should be looking along, in the words they
 * would use for it — and only when one line really is the reason.
 *
 * A block is drawn with its lower category down the side only when that
 * category is the spine; every other block is transposed. So fixing a value of
 * `alongCat` gives a row on the page in one case and a column in the other, and
 * getting that backwards is the whole bug this replaces.
 */
function gridReason(puzzle: Puzzle, cell: Cell, forced: Forced): string {
  if (forced.by === "combination") {
    return "No single row or column gives you this one. It comes from putting a few of your marks together.";
  }
  if (forced.by === "rule-out") {
    const bridge = puzzle.categories[forced.bridgeCat];
    const value = bridge?.values[forced.bridgeVal];
    const tiedIsRow = forced.tiedCat === cell.rowCat;
    const tied = tiedIsRow ? cell.rowCat : cell.colCat;
    const out = tiedIsRow ? cell.colCat : cell.rowCat;
    const tiedVal = tiedIsRow ? cell.rowVal : cell.colVal;
    const outVal = tiedIsRow ? cell.colVal : cell.rowVal;
    const name = (c: number, v: number) =>
      `${puzzle.categories[c]?.name} ${puzzle.categories[c]?.values[v]}`;
    return `You have ${bridge?.name} ${value} tied to ${name(tied, tiedVal)}, and ${bridge?.name} ${value} ruled out for ${name(out, outVal)}. So those two cannot go together.`;
  }
  if (forced.by === "carry") {
    const bridge = puzzle.categories[forced.bridgeCat];
    const value = bridge?.values[forced.bridgeVal];
    const [one, two] = [cell.rowCat, cell.colCat].map(
      (c) =>
        `${puzzle.categories[c]?.name} ${puzzle.categories[c]?.values[c === cell.rowCat ? cell.rowVal : cell.colVal]}`,
    );
    return `You have already tied ${bridge?.name} ${value} to ${one}, and ${bridge?.name} ${value} to ${two}. So those two go together as well.`;
  }
  const upright = drawnUpright(cell.rowCat, cell.colCat);
  const sideCat = upright ? cell.rowCat : cell.colCat;
  const line = forced.alongCat === sideCat ? "row" : "column";
  const label = puzzle.categories[forced.alongCat]?.values[forced.alongVal];
  const way = line === "row" ? "along" : "down";
  return `Look ${way} the ${label} ${line}. Every other cell in it is crossed out, so the one still open has to be the answer.`;
}

/**
 * A hint as the reader sees it, one stage at a time.
 *
 * Three lines rather than three separate messages: pressing for more should
 * add to what is already on screen, not replace it, so the reader can see how
 * far they chose to go.
 */
export function hintLines(puzzle: Puzzle, hint: Hint, stage: Stage, why?: string | null): string[] {
  switch (hint.kind) {
    case "mistake": {
      const where = hint.blocks.length === 1 ? hint.blocks[0] : hint.blocks.join(" and ");
      const count = hint.mistakes.length;
      const what = count === 1 ? "a mark" : `${count} marks`;
      return [
        `Before anything else: there is ${what} in ${where} that cannot be right. That happens to all of us, and it is the usual reason a grid stops moving. Have a look there and see which one to erase.`,
      ];
    }
    case "solved":
      return ["That is the whole grid, and every mark in it is right. Nicely done."];
    case "stuck":
      return [
        "We cannot see the next step from what is on the grid at the moment. If you rubbed out something you were sure about, putting it back will usually get things going again.",
      ];
    case "grid": {
      const lines = [
        `You will not need a clue for this one. Take another look at ${blockName(puzzle, hint.cell)}.`,
      ];
      if (stage >= 2) lines.push(gridReason(puzzle, hint.cell, hint.forced));
      if (stage >= 3) lines.push(theMark(puzzle, hint.cell, hint.mark));
      return lines;
    }
    case "clue": {
      const lines = [`Have another look at clue ${hint.clueIndex + 1}.`];
      if (stage >= 2) {
        lines.push(
          `It says "${hint.clue.text}" Now read that against what you already have in ${blockName(puzzle, hint.cell)}.`,
        );
      }
      if (stage >= 3) lines.push(theMark(puzzle, hint.cell, hint.mark));
      if (stage >= 4 && why) lines.push(why);
      return lines;
    }
  }
}

/** The last stage of any hint: which square, and what goes in it. */
function theMark(puzzle: Puzzle, cell: Cell, mark: "tick" | "cross"): string {
  const where = cellName(puzzle, cell);
  return mark === "tick" ? `That is ${where}. Give it a tick.` : `That is ${where}. Cross it out.`;
}

/**
 * Whether pressing for more would actually say anything new.
 *
 * `why` is passed in rather than worked out here: lib/explain.ts reads this
 * module, so this module cannot read it back. The caller has it to hand anyway,
 * since it needs it for `hintLines`.
 */
export function hasMoreStages(hint: Hint, stage: Stage, why?: string | null): boolean {
  if (hint.kind === "clue") return stage < (why ? 4 : 3);
  return hint.kind === "grid" && stage < 3;
}
